// Mirrors `TRASH_RETENTION_DAYS` in
// `artifacts/api-server/src/jobs/purgeOldTrashedTemplates.ts`. Kept as
// a literal here (rather than imported) so the web bundle doesn't
// reach into the server package; if the server window changes, bump
// this in lockstep.
export const TRASH_RETENTION_DAYS = 30;

// Start showing the "deletes in N days" countdown once a row is within
// this many days of the cutoff. Picked to roughly match the Gmail
// behavior of nudging the user only as the deadline approaches rather
// than from day one.
export const TRASH_COUNTDOWN_VISIBLE_DAYS = 7;

// Switch the countdown to the urgent (amber) treatment at or below
// this many days remaining so the row visibly nudges the user.
export const TRASH_COUNTDOWN_URGENT_DAYS = 3;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type TrashCountdown = { daysLeft: number; urgent: boolean };

/**
 * Compute the "deletes in N days" countdown for a trashed row.
 *
 * Returns `null` when the countdown should NOT be rendered:
 *   - `deletedAt` is missing or unparseable
 *   - the row is still further than `TRASH_COUNTDOWN_VISIBLE_DAYS` from
 *     the purge cutoff (no need to nag the user yet)
 *   - the row is already past the cutoff (the purge job will sweep it
 *     on its next tick; surfacing "deletes in 0 days" here would just
 *     be confusing)
 *
 * `daysLeft` is rounded UP so a row 0.4 days from the cutoff still
 * reads as "deletes in 1 day" instead of "0 days", matching how users
 * mentally round these warnings.
 */
export function getTrashCountdown(
  deletedAt: string | Date | null | undefined,
  now: Date = new Date(),
): TrashCountdown | null {
  if (!deletedAt) return null;
  const deleted = deletedAt instanceof Date ? deletedAt : new Date(deletedAt);
  const deletedMs = deleted.getTime();
  if (Number.isNaN(deletedMs)) return null;

  const cutoffMs = deletedMs + TRASH_RETENTION_DAYS * MS_PER_DAY;
  const remainingMs = cutoffMs - now.getTime();
  if (remainingMs <= 0) return null;

  const daysLeft = Math.ceil(remainingMs / MS_PER_DAY);
  if (daysLeft > TRASH_COUNTDOWN_VISIBLE_DAYS) return null;

  return { daysLeft, urgent: daysLeft <= TRASH_COUNTDOWN_URGENT_DAYS };
}
